import { useState, useEffect, useRef } from 'react';

interface Beat {
  id: string;
  title: string;
}

interface ShareBeatPopupProps {
  beat: Beat;
  onClose: () => void;
  anchorRect?: DOMRect | null;
}

export default function ShareBeatPopup({ beat, onClose, anchorRect }: ShareBeatPopupProps) {
  const [copied, setCopied] = useState(false);
  const popupRef = useRef<HTMLDivElement>(null);

  const shareUrl = `${window.location.origin}${window.location.pathname}?beat=${encodeURIComponent(beat.id)}#beats`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const el = document.createElement('textarea');
      el.value = shareUrl;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleTwitter = () => {
    const text = encodeURIComponent(`Check out this beat: "${beat.title}" 🔥`);
    const url = encodeURIComponent(shareUrl);
    window.open(`https://twitter.com/intent/tweet?text=${text}&url=${url}`, '_blank', 'noopener,noreferrer');
  };

  const handleWhatsApp = () => {
    const text = encodeURIComponent(`Check out this beat: "${beat.title}" 🔥 ${shareUrl}`);
    window.open(`https://wa.me/?text=${text}`, '_blank', 'noopener,noreferrer');
  };

  const handleNativeShare = () => {
    if (navigator.share) {
      navigator.share({
        title: `Beat: ${beat.title}`,
        text: `Check out this beat: "${beat.title}" 🔥`,
        url: shareUrl,
      }).catch(() => {});
    }
  };

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  // Position popup above anchor if possible
  const style: React.CSSProperties = {};
  if (anchorRect) {
    const spaceBelow = window.innerHeight - anchorRect.bottom;
    if (spaceBelow < 200) {
      style.bottom = window.innerHeight - anchorRect.top + 6;
      style.top = 'auto';
    }
  }

  return (
    <div
      ref={popupRef}
      className="absolute z-50 right-0 mt-2 w-64 bg-white rounded-2xl border border-gray-100 overflow-hidden"
      style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.12)', ...style }}
    >
      {/* Header */}
      <div className="px-4 pt-4 pb-3 border-b border-gray-100">
        <div className="flex items-center justify-between mb-0.5">
          <span className="text-sm font-bold text-gray-900">Share Beat</span>
          <button
            onClick={onClose}
            className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-700 transition-colors cursor-pointer rounded-full hover:bg-gray-100"
          >
            <i className="ri-close-line text-sm"></i>
          </button>
        </div>
        <p className="text-xs text-gray-500 truncate">{beat.title}</p>
      </div>

      {/* Share options */}
      <div className="p-3 space-y-1.5">
        {/* Copy Link */}
        <button
          onClick={handleCopy}
          className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
            copied
              ? 'bg-green-50 text-green-700'
              : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
          }`}
        >
          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-200 text-gray-700 flex-shrink-0">
            <i className={`${copied ? 'ri-checkbox-circle-fill text-green-600' : 'ri-link text-base'}`}></i>
          </div>
          {copied ? 'Link Copied!' : 'Copy Link'}
        </button>

        {/* Twitter / X */}
        <button
          onClick={handleTwitter}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium bg-gray-50 text-gray-700 hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
        >
          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-900 text-white flex-shrink-0">
            <i className="ri-twitter-x-line text-base"></i>
          </div>
          Share on X
        </button>

        {/* WhatsApp */}
        <button
          onClick={handleWhatsApp}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium bg-gray-50 text-gray-700 hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
        >
          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-green-500 text-white flex-shrink-0">
            <i className="ri-whatsapp-line text-base"></i>
          </div>
          Share on WhatsApp
        </button>

        {/* Native share (mobile) */}
        {typeof navigator !== 'undefined' && 'share' in navigator && (
          <button
            onClick={handleNativeShare}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium bg-gray-50 text-gray-700 hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
          >
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-red-600 text-white flex-shrink-0">
              <i className="ri-share-forward-line text-base"></i>
            </div>
            More Options
          </button>
        )}
      </div>

      {/* URL preview */}
      <div className="px-3 pb-3">
        <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg border border-gray-100">
          <i className="ri-link text-gray-400 text-xs flex-shrink-0"></i>
          <span className="text-xs text-gray-400 truncate flex-1">{shareUrl}</span>
        </div>
      </div>
    </div>
  );
}
